import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { LogoLoader } from '@/components/logo-loader';
import {
	$addUssds,
	$cursorUssds,
	$hasMoreUssds,
	$isFetchingUssds,
	$setCursorUssds,
	$setHasMoreUssds,
	$setIsFetchingUssds,
	$setUssds,
	$ussds,
	type IUssd,
} from '@/entities/ussd.entity';
import {
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$ussdFetchKey,
	$ussdListScrollTop,
	$ussdSearchField,
	$ussdSortBy,
	$setUssdFetchKey,
	$setUssdListScrollTop,
	onlineStatusToIsOnline,
	toServerUssdFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { mockGlobalUssds } from '@/mocks/fixtures';
import UssdCard from '../cards/ussd-card';

const LIMIT = 35;
const SCROLL_COMPENSATION_THRESHOLD = 80;

export default function UssdList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const prevCountRef = useRef(0);
	const prevFirstIdRef = useRef<number | null>(null);

	const [ussds, addUssds, setUssds] = useUnit([$ussds, $addUssds, $setUssds]);
	const [hasMore, setHasMore] = useUnit([$hasMoreUssds, $setHasMoreUssds]);
	const [cursor, setCursor] = useUnit([$cursorUssds, $setCursorUssds]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingUssds, $setIsFetchingUssds]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$ussdSearchField,
		$ussdSortBy,
		$clientsSortDir,
		$ussdListScrollTop,
		$setUssdListScrollTop,
		$ussdFetchKey,
		$setUssdFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerUssdFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: ussds.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 130,
		overscan: 8,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'ussd',
		listRef,
		virtualizer,
		items: ussds,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && ussds.length > 0,
	});

	useEffect(() => {
		if (isDevMode) {
			setUssds(mockGlobalUssds);
			setHasMore(false);
			return;
		}

		function addUssdHandler(data: { ussds: IUssd[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.ussds?.length) {
				addUssds(data.ussds);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addUssd, addUssdHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addUssd, addUssdHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setUssds([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getUssd, { ...queryPayload, cursor: null });
		}

		if (ussds.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);
			return () => clearTimeout(timeout);
		}
	}, [currentFetchKey]);

	useLayoutEffect(() => {
		const firstId = ussds[0]?.id ?? null;
		const isPrepend =
			ussds.length === prevCountRef.current + 1 && prevCountRef.current > 0 && firstId !== prevFirstIdRef.current;

		if (isPrepend && listRef.current) {
			const el = listRef.current.querySelector('[data-index="0"]') as HTMLElement | null;
			const height = el?.getBoundingClientRect().height ?? 130;

			if (listRef.current.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				listRef.current.scrollTop += height + 8;
			}
		}

		prevCountRef.current = ussds.length;
		prevFirstIdRef.current = firstId;
	}, [ussds]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= ussds.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getUssd, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, ussds.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !ussds.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : ussds.length === 0 ? (
				<div className='text-center mt-30 text-md'>USSD не найдены</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const ussd = ussds[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<UssdCard ussd={ussd} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
